import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/shared/PageHeader';
import { DataTable, type Column } from '@/components/shared/DataTable';
import { PlanBadge } from '@/components/billing/PlanBadge';
import { LifecycleBadge } from '@/components/admin/LifecycleBadge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { usePlatformOrganizations } from '@/hooks/usePlatform';
import { normalizePlanTier } from '@/types/billing';
import { formatDate, formatNumber, formatRelativeDate } from '@/lib/formatters';
import type { PlatformOrgListItem } from '@/types/platform';

const ALL = '__all__';
const PER_PAGE = 25;

export function AdminOrganizationsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [plan, setPlan] = useState<string>(ALL);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => setPage(1), [search, plan]);

  const { data, isLoading } = usePlatformOrganizations({
    page,
    per_page: PER_PAGE,
    search: search || undefined,
    plan: plan === ALL ? undefined : plan,
  });

  const rows = data?.data ?? [];
  const pagination = data?.meta.pagination;

  const columns: Column<PlatformOrgListItem>[] = [
    {
      key: 'name',
      header: 'Organization',
      render: (o) => <span className="font-medium text-foreground">{o.name}</span>,
    },
    { key: 'plan', header: 'Plan', render: (o) => <PlanBadge tier={normalizePlanTier(o.plan)} /> },
    { key: 'stage', header: 'Stage', render: (o) => <LifecycleBadge stage={o.lifecycle_stage} /> },
    {
      key: 'activation',
      header: 'Activation',
      render: (o) => (
        <div className="flex items-center gap-2 min-w-[120px]">
          <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary"
              style={{
                width: `${(o.activation_completed_count / o.activation_total_count) * 100}%`,
              }}
            />
          </div>
          <span className="text-xs text-muted-foreground tabular-nums">
            {o.activation_completed_count}/{o.activation_total_count}
          </span>
        </div>
      ),
    },
    { key: 'users', header: 'Users', render: (o) => o.user_count },
    {
      key: 'retr',
      header: 'Retrievals (30d)',
      render: (o) => formatNumber(o.retrieval_count_30d),
    },
    {
      key: 'created',
      header: 'Signed up',
      render: (o) => (o.created_at ? formatDate(o.created_at) : '—'),
    },
    {
      key: 'active',
      header: 'Last active',
      render: (o) => (o.last_active_at ? formatRelativeDate(o.last_active_at) : 'Never'),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Platform Admin" title="Organizations" subtitle="Every signup and its funnel state." />

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search organizations…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="max-w-xs"
        />
        <Select value={plan} onValueChange={setPlan}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All plans</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="team">Team</SelectItem>
            <SelectItem value="growth">Growth</SelectItem>
            <SelectItem value="enterprise">Enterprise</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          keyExtractor={(o) => o.id}
          onRowClick={(o) => navigate(`/admin/organizations/${o.id}`)}
          emptyMessage="No organizations match the current filters."
        />
      )}

      {pagination && pagination.total_pages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Showing {rows.length} of {pagination.total} · page {pagination.page} of{' '}
            {pagination.total_pages}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pagination.total_pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
